import { AxiosPromise } from "axios";
import { anonymousApi, authorizedApi, BaseApi } from "../../shared/api/BaseApi";
import { CreateNewCustomerMeetingDto } from "./api/CreateNewCustomerMeetingDto";
import { CustomerDto, CustomerMeetingDto, PersonDto } from "./api/CustomerMeetingDto";
import { MeetingLogType } from "../store/MeetingLogType";
import { MeetingDeclineReason } from "../../shared/types/MeetingDeclineReason";
import { BusinessLine } from "../../shared/types/BusinessLine";
import { LanguageVersion } from "../../shared/types/LanguageVersion";
import { IKeyValueMap } from "mobx";
import { ICustomer } from "../../models/Customer";
import { MeetingKind } from "../../shared/types/MeetingKind";

export interface CreateCustomerMeetingData {
    subject: string;
    customers: CustomerDto[];
    startAt: Date;
    endAt: Date;
    additionalAttendeesEmails: string[];
    businessLine: BusinessLine;
    languageVersion: LanguageVersion;
    businessLineLanguageVersion: LanguageVersion;
    companyName?: string;
    c6?: string;
    regon?: string;
    clientsIds?: string[];
    meetingKind: MeetingKind;
    externalConsultants?: PersonDto[];
    thirdParties?: PersonDto[];
}

export interface UpdateCustomerMeetingData {
    urlHash: string;
    customers: CustomerDto[];
    startAt: Date;
    endAt: Date;
    additionalAttendeesEmails: string[];
    externalConsultants?: PersonDto[];
    thirdParties?: PersonDto[];
}

export interface UpdateCustomerMeetingTypeData {
    urlHash: string;
    meetingType?: number;
}

export interface MeetingData {
    urlHash: string;
}

export interface MeetingLogData {
    meetingId: string;
    logType: MeetingLogType;
}

export interface CancelMeetingData {
    urlHash: string;
    reason: MeetingDeclineReason;
}

export interface MeetingOptionsDto {
    meetingTypeList: MeetingTypeDictionaryItemDto[];
    showMeetingKindToggle: boolean;
    defaultMeetingKind: MeetingKind;
    meetingKindList: MeetingKind[];
    maxNumberOfDaysToPlanMeeting: number;
}

export interface MeetingTypeDictionaryItemDto {
    key: string;
    value: number;
}
export interface MeetingTypeDto {
    value?: number;
}

export interface CustomerMeetingsService {
    cancelMeeting(data: CancelMeetingData): AxiosPromise;
    createMeeting(data: CreateCustomerMeetingData): AxiosPromise<CreateNewCustomerMeetingDto>;
    updateMeeting(data: UpdateCustomerMeetingData): AxiosPromise;
    updateMeetingType(data: UpdateCustomerMeetingTypeData): AxiosPromise;
    getMeetings(): AxiosPromise<CustomerMeetingDto[]>;
    getMeetingOptions(language: string): AxiosPromise<MeetingOptionsDto>;
    getMeetingType(meetingId: string): AxiosPromise<MeetingTypeDto>;
    addLog(data: MeetingLogData): AxiosPromise;
}

export class DefaultCustomerMeetingsService implements CustomerMeetingsService {
    private cancelMeetingUrl: string = "/customer-meetings/Decline";
    private createMeetingUrl: string = "/customer-meetings/Create";
    private updateMeetingUrl: string = "/customer-meetings/Update";
    private updateMeetingTypeUrl: string = "/customer-meetings/UpdateMeetingType";
    private meetingsUrl: string = "/customer-meetings";
    private getMeetingsUrl: string = "/customer-meetings/GetMeetings";
    private addLogUrl: string = "/customer-meetings/AddLog";

    constructor(private authorizedApi: BaseApi) {}

    cancelMeeting(data: CancelMeetingData): AxiosPromise {
        return this.authorizedApi.post(this.cancelMeetingUrl, data);
    }

    createMeeting(data: CreateCustomerMeetingData): AxiosPromise<CreateNewCustomerMeetingDto> {
        return this.authorizedApi.post<CreateNewCustomerMeetingDto>(this.createMeetingUrl, data);
    }

    updateMeeting(data: UpdateCustomerMeetingData): AxiosPromise {
        return this.authorizedApi.post(this.updateMeetingUrl, data);
    }

    updateMeetingType(data: UpdateCustomerMeetingTypeData): AxiosPromise {
        return this.authorizedApi.post(this.updateMeetingTypeUrl, data);
    }

    getMeetings(): AxiosPromise<CustomerMeetingDto[]> {
        return this.authorizedApi.get<CustomerMeetingDto[]>(this.getMeetingsUrl, {});
    }

    getMeetingOptions(language: string): AxiosPromise<MeetingOptionsDto> {
        return this.authorizedApi.get<MeetingOptionsDto>(
            `${this.meetingsUrl}/GetMeetingOptions/?language=${language}`,
            {}
        );
    }

    getMeetingType(meetingId: string): AxiosPromise<MeetingTypeDto> {
        return this.authorizedApi.get<MeetingTypeDto>(`${this.meetingsUrl}/GetMeetingType/${meetingId}`, {});
    }

    addLog(data: MeetingLogData): AxiosPromise {
        return this.authorizedApi.post(this.addLogUrl, data);
    }
}

export class CustomerMeetingsServiceWithResponse implements CustomerMeetingsService {
    _response: any = {
        data: this.response,
    };

    constructor(private response: any) {}

    cancelMeeting() {
        return Promise.resolve(this._response);
    }

    createMeeting() {
        return Promise.resolve(this._response);
    }

    updateMeeting() {
        return Promise.resolve(this._response);
    }

    updateMeetingType() {
        return Promise.resolve(this._response);
    }

    getMeetings() {
        return Promise.resolve(this._response);
    }

    getMeetingOptions() {
        return Promise.resolve(this._response);
    }

    getMeetingType() {
        return Promise.resolve(this._response);
    }

    addLog() {
        return Promise.resolve(this._response);
    }
}

export default new DefaultCustomerMeetingsService(authorizedApi);
